<script setup>
import { isNull } from 'lodash'
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import CustomDashboardApiHandler from '@/api/custom-dashboard'
import { SELECT_TYPES } from '@/utils/components-configurations/app-select'
import { handleError } from '@/utils/error-handling'
import { showNotify } from '@/utils/notify'
import { localSearch } from '@/utils/objectives'

import AppDialog from '@/components/AppDialog'
import AppFieldError from '@/components/form/AppFieldError'
import FormFieldNext from '@/components/form/FormFieldNext'
import AppSelect from '@/components/ui/AppSelect/AppSelect'

defineOptions({
  name: 'AddToCustomDashboardModal'
})

const props = defineProps({
  opened: {
    type: Boolean
  },

  dashboardsList: {
    type: Array,
    required: true
  },

  selectedElementIds: {
    type: Array,
    required: true
  }
})

const emit = defineEmits({
  'on-close': null
})

const isLoading = ref(false)
const isDashboardError = ref(false)
const isModuleError = ref(false)

const { t } = useI18n()

const onConfirm = async () => {
  if (isNull(selectedDashboard.value)) {
    isDashboardError.value = true
  }

  if (isNull(selectedModule.value)) {
    isModuleError.value = true
  }

  if (isDashboardError.value || isModuleError.value) return

  if (isLoading.value) return

  const api = new CustomDashboardApiHandler()
  try {
    isLoading.value = true
    await api.addElementsToDashboard({
      dashboardId: selectedDashboard.value,
      moduleId: selectedModule.value,
      elementIds: props.selectedElementIds
    })
    showNotify({
      expanded: true,
      content: t('dashboard.successfully_added_to_dashboard', {
        count: props.selectedElementIds.length,
        dashboardName: props.dashboardsList.find(({ id }) => id === selectedDashboard.value).name,
        moduleName: modulesList.value.find(({ id }) => id === selectedModule.value).name
      })
    })
    onClose()
  } catch (error) {
    handleError({ error })
  } finally {
    isLoading.value = false
  }
}

const onClose = () => {
  emit('on-close', false)
}

const selectedDashboard = ref(null)
const selectedModule = ref(null)

const dashboardSelectReference = useTemplateRef('dashboard-select')

const onAfterEnter = async () => {
  await nextTick()
  dashboardSelectReference.value.focus()
}

const removeDashboardError = () => {
  isDashboardError.value = false
}

const removeModuleError = () => {
  isModuleError.value = false
}

const removeErrors = () => {
  removeDashboardError()
  removeModuleError()
}

watch(
  () => props.opened,
  async opened => {
    if (!opened) {
      selectedDashboard.value = null
      selectedModule.value = null
      removeErrors()
    }
  }
)

const modulesList = computed(() => {
  const selectedDashboardEntity = props.dashboardsList.find(
    ({ id }) => id === selectedDashboard.value
  )

  if (!selectedDashboardEntity) return []

  return selectedDashboardEntity.modules
})

const moduleSelectRefrence = useTemplateRef('module-select')

const onSelectDashboard = async () => {
  selectedModule.value = null
  await nextTick()
  moduleSelectRefrence.value.focus()
}
</script>

<template>
  <AppDialog
    :loading="isLoading"
    :show="opened"
    :title="t('dashboard.add_to_custom_dashboard')"
    hide-hero
    @on-confirm="onConfirm"
    @on-close="onClose"
    @after-enter="onAfterEnter"
  >
    <div class="atd-Body">
      <FormFieldNext :label="t('dashboard.select_custom_dashboard')">
        <div>
          <AppSelect
            ref="dashboard-select"
            v-model="selectedDashboard"
            :is-error="isDashboardError"
            :offset="[0, -40]"
            :options="dashboardsList"
            :search-function="value => localSearch({ value, options: dashboardsList })"
            :type="SELECT_TYPES.MODERN"
            dropdown-search
            item-label="name"
            item-value="id"
            @open="removeDashboardError"
            @update:model-value="onSelectDashboard"
          >
            <template #button-content="{ option }">
              <span class="oboard-truncated-text">
                {{ option?.name || t('dashboard.select_custom_dashboard') }}
              </span>
            </template>
          </AppSelect>

          <AppFieldError v-if="isDashboardError" :show="isDashboardError">
            {{ t('field.required') }}
          </AppFieldError>
        </div>
      </FormFieldNext>

      <FormFieldNext :label="t('dashboard.select_module')">
        <div>
          <AppSelect
            ref="module-select"
            v-model="selectedModule"
            :is-error="isModuleError"
            :offset="[0, -40]"
            :options="modulesList"
            :search-function="value => localSearch({ value, options: modulesList })"
            :type="SELECT_TYPES.MODERN"
            dropdown-search
            item-label="name"
            item-value="id"
            @open="removeModuleError"
          >
            <template #button-content="{ option }">
              <span class="oboard-truncated-text">
                {{ option?.name || t('dashboard.select_module') }}
              </span>
            </template>
          </AppSelect>

          <AppFieldError v-if="isModuleError" :show="isModuleError">
            {{ t('field.required') }}
          </AppFieldError>
        </div>
      </FormFieldNext>
    </div>
  </AppDialog>
</template>

<style lang="scss" scoped>
.atd-Body {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
</style>
